/**
 * TouchTarget - IMPROVEMENT 28
 * Ensures minimum 44x44px touch targets for accessibility.
 */
import { forwardRef, ReactNode, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react'
import clsx from 'clsx'
import './TouchTarget.css'

interface TouchTargetProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'ghost' | 'primary'
  rounded?: boolean
  active?: boolean
  asChild?: boolean
}

export const TouchTarget = forwardRef<HTMLButtonElement, TouchTargetProps>(({
  children,
  size = 'md',
  variant = 'default',
  rounded = false,
  active = false,
  className,
  disabled,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      className={clsx(
        'touch-target',
        `touch-target--${size}`,
        `touch-target--${variant}`,
        rounded && 'touch-target--rounded',
        active && 'touch-target--active',
        disabled && 'touch-target--disabled',
        className
      )}
      disabled={disabled}
      {...props}
    >
      <span className="touch-target-content">{children}</span>
    </button>
  )
})

TouchTarget.displayName = 'TouchTarget'

// Touch-friendly link
interface TouchLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode
  size?: 'sm' | 'md' | 'lg'
  active?: boolean
}

export const TouchLink = forwardRef<HTMLAnchorElement, TouchLinkProps>(({
  children,
  size = 'md',
  active = false,
  className,
  ...props
}, ref) => {
  return (
    <a
      ref={ref}
      className={clsx(
        'touch-target touch-target--link',
        `touch-target--${size}`,
        active && 'touch-target--active',
        className
      )}
      {...props}
    >
      <span className="touch-target-content">{children}</span>
    </a>
  )
})

TouchLink.displayName = 'TouchLink'

// Icon-only touch target
interface TouchIconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: ReactNode
  label: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'ghost' | 'primary' | 'danger'
  active?: boolean
}

export const TouchIconButton = forwardRef<HTMLButtonElement, TouchIconButtonProps>(({
  icon,
  label,
  size = 'md',
  variant = 'ghost',
  active = false,
  className,
  disabled,
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      className={clsx(
        'touch-icon-button',
        `touch-icon-button--${size}`,
        `touch-icon-button--${variant}`,
        active && 'touch-icon-button--active',
        disabled && 'touch-icon-button--disabled',
        className
      )}
      disabled={disabled}
      aria-label={label}
      title={label}
      {...props}
    >
      {icon}
    </button>
  )
})

TouchIconButton.displayName = 'TouchIconButton'

// Swipe list item with touch-friendly actions
interface SwipeableListItemProps {
  children: ReactNode
  leadingActions?: ReactNode
  trailingActions?: ReactNode
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  className?: string
}

export function SwipeableListItem({
  children,
  leadingActions,
  trailingActions,
  className
}: SwipeableListItemProps) {
  return (
    <div className={clsx('swipeable-list-item', className)}>
      {leadingActions && (
        <div className="swipeable-list-item-leading">{leadingActions}</div>
      )}
      <div className="swipeable-list-item-content">{children}</div>
      {trailingActions && (
        <div className="swipeable-list-item-trailing">{trailingActions}</div>
      )}
    </div>
  )
}

// Touch-friendly checkbox
interface TouchCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  className?: string
}

export function TouchCheckbox({
  checked,
  onChange,
  label,
  disabled = false,
  className
}: TouchCheckboxProps) {
  return (
    <label className={clsx('touch-checkbox', disabled && 'touch-checkbox--disabled', className)}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="touch-checkbox-box">
        {checked && (
          <svg viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {label && <span className="touch-checkbox-label">{label}</span>}
    </label>
  )
}

// Touch-friendly toggle/switch
interface TouchToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
}

export function TouchToggle({
  checked,
  onChange,
  label,
  disabled = false,
  size = 'md',
  className
}: TouchToggleProps) {
  return (
    <label className={clsx('touch-toggle', `touch-toggle--${size}`, disabled && 'touch-toggle--disabled', className)}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="touch-toggle-track">
        <span className="touch-toggle-thumb" />
      </span>
      {label && <span className="touch-toggle-label">{label}</span>}
    </label>
  )
}

export default TouchTarget
